import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { Box, Button, Divider, Typography } from "@mui/material";

import APP_ROUTES from "../../../../../routers/routeConstants";

import { CreateProgramFormData } from "../../../types/service.type";
import { createProgramSchema } from "../../../schemas/service.schema";
import { ArrowBackButtonIcon } from "../../../assets/icons/go-hire-icons";
import { useCreateProgram } from "../../../api/mutations/useCreateProgram";
import { CurrencyEnum, ServiceDeliveryEnum, ServiceRequestTypeEnum, ServiceTypeEnum } from "../../../enum/service.enum";

import { ProgramInformation, ProgramContentAndTerms, DetailsAndPricing } from "./Steps";
import { useMinimumServicePrices } from "../../../../../api/queries/payment/paymentQueries";

export type CreateServiceStep = "programInformation" | "detailsAndPricing" | "programContentAndTerms";

const createServiceSteps: CreateServiceStep[] = ["programInformation", "detailsAndPricing", "programContentAndTerms"];

const CreateProgram = () => {
  const { t, i18n } = useTranslation("goService", { keyPrefix: "createServicePage" });
  const [currentStep, setCurrentStep] = useState<CreateServiceStep>("programInformation");
  const navigate = useNavigate();

  let stepComponent: React.ReactNode = null;

  if (currentStep === "programInformation") stepComponent = <ProgramInformation />;
  if (currentStep === "programContentAndTerms") stepComponent = <ProgramContentAndTerms />;
  if (currentStep === "detailsAndPricing") stepComponent = <DetailsAndPricing />;

  const handleCancel = () => navigate(APP_ROUTES.CREATE_SERVICE);

  const handlePrevious = () => {
    const currentStepIndex = createServiceSteps.indexOf(currentStep);
    if (currentStepIndex > 0) {
      setCurrentStep(createServiceSteps[currentStepIndex - 1]);
    }
  };

  const handleBackButton = () => {
    if (currentStep === createServiceSteps[0]) {
      handleCancel();
    } else {
      handlePrevious();
    }
  };

  const methods = useForm<CreateProgramFormData>({
    resolver: zodResolver(createProgramSchema),
    mode: "all",
    defaultValues: {
      type: ServiceTypeEnum.Program,
      availability: {
        days: [],
        months: [],
        locations: [],
        alwaysAvailable: true,
        availableAllDay: false,
        from: "",
        to: "",
        option: ServiceDeliveryEnum.Online,
      },
      paymentConfig: {
        currency: CurrencyEnum.EGP,
        type: ServiceRequestTypeEnum.Fixed,
        amount: undefined,
        notExpired: true,
      },
    },
  });

  const { mutate: createProgram } = useCreateProgram((data) => {
    navigate(APP_ROUTES.CREATE_SERVICE, {
      replace: true,
      state: { serviceCreated: true, serviceId: data.id, serviceType: data.type },
    });
  });

  const { minimumServicePrices } = useMinimumServicePrices();

  const handleNext = async () => {
    let isValid = false;

    if (currentStep === createServiceSteps[0]) {
      isValid = await methods.trigger(["name", "description", "category", "subCategory", "level"]);
      if (!isValid) return;

      setCurrentStep(createServiceSteps[1]);
    } else if (currentStep === createServiceSteps[1]) {
      isValid = await methods.trigger([
        "numberOfVideos",
        "idealCompletionTime",
        "paymentConfig.amount",
        "paymentConfig.currency",
      ]);
      if (!isValid) return;

      const amount = methods.getValues("paymentConfig.amount");
      const currency = methods.getValues("paymentConfig.currency");
      const minimumServicePrice = minimumServicePrices?.[currency as keyof typeof minimumServicePrices];
      if (minimumServicePrice && amount < minimumServicePrice) {
        isValid = false;
        methods.setError("paymentConfig.amount", {
          message: "AMOUNT_MUST_BE_GREATER_THAN_MINIMUM_SERVICE_PRICE",
        });
        return;
      }
      setCurrentStep(createServiceSteps[2]);
    } else if (currentStep === createServiceSteps[2]) {
      isValid = await methods.trigger(["programContents", "allowGoalngoMemebers", "agreeToTerms"]);
      if (!isValid) return;

      const formData = methods.getValues();
      createProgram(formData);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box sx={styles.container}>
        <Button
          startIcon={<ArrowBackButtonIcon />}
          variant="text-md"
          onClick={handleCancel}
          sx={styles.backButton(i18n.language === "ar")}
        >
          {t("createCourse")}
        </Button>
        <Box sx={styles.contentContainer}>
          <Box sx={styles.titleContainer}>
            <Typography sx={styles.title}>{t(currentStep)}</Typography>
            <Box sx={styles.progressContainer}>
              {createServiceSteps.map((step, index) => (
                <Box
                  key={index}
                  sx={styles.progressLine(
                    currentStep === createServiceSteps[3]
                      ? index <= 3
                      : currentStep === createServiceSteps[2]
                      ? index <= 2
                      : currentStep === createServiceSteps[1]
                      ? index <= 1
                      : index === 0
                  )}
                />
              ))}
            </Box>
          </Box>
          <Divider sx={styles.divider} />
          {stepComponent}
          <Divider sx={styles.divider} />
          <Box sx={styles.footerContainer}>
            <Button variant="outlined-md" onClick={handleBackButton}>
              {currentStep === createServiceSteps[0] ? t("cancel") : t("previous")}
            </Button>
            <Button
              variant="primary-md"
              onClick={handleNext}
              type={currentStep === createServiceSteps[2] ? "submit" : "button"}
            >
              {currentStep === createServiceSteps[2] ? t("publish") : t("next")}
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default CreateProgram;

const styles = {
  container: {
    width: { xs: "100%", lg: "869px" },
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  contentContainer: {
    backgroundColor: "var(--Base-White, #FFF)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    border: "1px solid #E4E7EC",
  },
  divider: {
    mx: "-20px",
  },
  footerContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },
  backButton: (isArabic: boolean) => ({
    width: "fit-content",
    background: "transparent !important",
    color: "var(--black-100, #1C1C1C) !important",
    fontFamily: "Inter",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "30px",
    gap: "8px",
    "& .MuiButton-startIcon": {
      transform: isArabic ? "scaleX(-1)" : "scaleX(1)",
    },
  }),
  title: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  progressContainer: {
    display: "flex",
    gap: "4px",
    width: "100%",
  },
  progressLine: (isCompleted: boolean) => ({
    flex: 1,
    height: "4px",
    backgroundColor: isCompleted ? "var(--Brand-600, #008B07)" : "var(--Gray-200, #E4E7EC)",
    borderRadius: "2px",
  }),
};
